import { istDateTime } from "../../lib/format.js";
import { type SignalRating, useSignalRating } from "../../lib/signal-rating.js";

function pct(value: number | null): string {
  return value === null ? "—" : `${Math.round(value * 100)}%`;
}

const CONVICTION = ["very weak", "weak", "average", "strong", "exceptional"] as const;

/** The 0..1 conviction back onto the five words the question asked about. */
function convictionWord(value: number | null): string {
  if (value === null) return "—";
  return CONVICTION[Math.min(4, Math.round(value * 4))] ?? "—";
}

/**
 * Who gave a rating. The read shows the newest rating whichever model gave
 * it, so a signal Jev rated before the switch to Clef still says so.
 */
function raterOf(rating: SignalRating | null): string {
  return rating?.modelVersion.startsWith("jev") ? "Jev" : "Clef";
}

/** The rating, or why there is none. Exported for tests: no query client needed. */
export function ClefRatingView({ rating }: { rating: SignalRating | null }) {
  return (
    <section className="flex flex-col gap-1" data-testid="clef-rating">
      <h3 className="text-label uppercase text-text-muted">
        {raterOf(rating)}'s read · shadow mode
      </h3>
      {rating === null ? (
        <p className="text-body-sm text-text-muted">
          Not rated. Clef only rates signals that name a trade, and only while a token is
          configured.
        </p>
      ) : (
        <>
          <p className="text-data font-data tabular-nums text-text">
            take {pct(rating.takeProbability)} · setup {convictionWord(rating.conviction)}
          </p>
          <p className="text-data-sm text-text-muted">
            {rating.modelVersion}, {istDateTime(rating.ratedAt)}. Not yet scored against outcomes,
            so read it as a second opinion, not a filter.
          </p>
        </>
      )}
    </section>
  );
}

/** Clef's rating of one signal, fetched on its own; see `lib/signal-rating.ts`. */
export function ClefRating({ signalId }: { signalId: string }) {
  const query = useSignalRating(signalId);
  // A failed or pending read shows nothing: this is a second opinion, and a
  // loading line or an error under every signal would outweigh it.
  if (!query.data) return null;
  return <ClefRatingView rating={query.data.rating} />;
}
